import React, { useCallback, useEffect, useMemo } from "react";
import { connect } from "react-redux";

import { faFilter } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useWatch } from "antd/es/form/Form";
import useFormInstance from "antd/es/form/hooks/useFormInstance";
import { useIntl } from "react-intl";

import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import { FormItem, SelectField } from "@mapmycustomers/ui";

import stageIcon from "@app/assets/icons/deal-stage-dark.svg";
import { ConditionalFormFieldProps } from "@app/component/ConditionalFormField";
import EntityBadge from "@app/component/EntityBadge";
import { getFunnels, getFunnelStages } from "@app/store/deal";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";
import dealFieldModel, { DealFieldName } from "@app/util/fieldModel/DealFieldModel";
import defaultSelectOptionMapper from "@app/util/mappers/defaultSelectOptionMapper";

import styles from "./FunnelStageField.module.scss";

interface FunnelStageFieldProps extends ConditionalFormFieldProps {
  autoSelectFunnel?: boolean;
  defaultFunnelId?: Funnel["id"];
  defaultStageId?: Stage["id"];
  disabled?: boolean;
  required?: boolean;
}

const FunnelStageField = ({
  autoSelectFunnel = true,
  defaultFunnelId,
  defaultStageId,
  disabled,
  funnels,
  funnelStages,
  isCreateForm,
  required,
}: FunnelStageFieldProps & ReturnType<typeof mapStateToProps>) => {
  const intl = useIntl();
  const form = useFormInstance();
  const analytics = useAnalytics();

  const { funnelLabel, stageLabel } = useMemo(
    () => ({
      funnelLabel: dealFieldModel.getByName(DealFieldName.FUNNEL)!.displayName,
      stageLabel: dealFieldModel.getByName(DealFieldName.STAGE)!.displayName,
    }),
    []
  );

  const funnelId: Funnel["id"] | undefined = useWatch("funnelId", form);
  const funnelOptions = useMemo(() => funnels.map(defaultSelectOptionMapper), [funnels]);
  const stageOptions = useMemo(
    () =>
      (funnelId ? (funnelStages[funnelId] ?? []) : []).map((stage) => ({
        key: stage.id,
        label: <EntityBadge entity={stage} />,
        value: stage.id,
      })),
    [funnelStages, funnelId]
  );

  const stageField = dealFieldModel.getByName(DealFieldName.STAGE);
  const isStageFieldDisabled = !stageField?.isEditable && !isCreateForm;

  const handleFunnelChange = useCallback(
    (funnelId?: Funnel["id"]) => {
      form.setFieldsValue({ funnelId, stageId: undefined });
      // scope comes from the surface's AnalyticsContext, e.g. "Using: Add New Deal -> Select
      // Funnel" in the Create Deal modal; fires only on user changes (setFieldsValue bypasses it)
      analytics.using(["Select Funnel"], {
        funnel: funnels.find((funnel) => funnel.id === funnelId)?.name,
      });
    },
    [analytics, form, funnels]
  );

  const handleStageChange = useCallback(
    (stageId: Stage["id"]) => form.setFieldsValue({ stageId }),
    [form]
  );

  // Setting Default Funnel if given or the first funnel otherwise
  useEffect(() => {
    if (funnels.length && !funnelId && autoSelectFunnel) {
      form.setFieldsValue({
        funnelId: defaultFunnelId,
        // only set stage when both default funnel and stage are specified
        stageId: defaultFunnelId && defaultStageId ? defaultStageId : undefined,
      });
    }
  }, [autoSelectFunnel, defaultFunnelId, defaultStageId, form, funnelId, funnels]);

  return (
    <div className={styles.container} data-id="js-funnel-staging-field">
      <FormItem
        label={funnelLabel}
        name="funnelId"
        required={required}
        rules={[{ required }]}
        showLabel={false}
      >
        <SelectField<Funnel["id"]>
          dropdownMatchSelectWidth={false}
          label={funnelLabel}
          locked={disabled}
          onChange={handleFunnelChange}
          options={funnelOptions}
          placeholder={intl.formatMessage({
            id: "formFields.funnelStageField.funnel.placeholder",
            defaultMessage: "Select",
            description: "Funnel field placeholder",
          })}
          prefixIcon={<FontAwesomeIcon icon={faFilter} />}
          required={required}
        />
      </FormItem>

      <FormItem
        label={stageLabel}
        name={"stageId"}
        required={required}
        rules={[{ required }]}
        showLabel={false}
      >
        <SelectField<Stage["id"]>
          dropdownMatchSelectWidth={false}
          label={stageLabel}
          locked={isStageFieldDisabled}
          onChange={handleStageChange}
          options={stageOptions}
          placeholder={intl.formatMessage({
            id: "formFields.funnelStageField.stage.placeholder",
            defaultMessage: "Select",
            description: "Stage field placeholder",
          })}
          prefixIcon={<img alt="stage icon" className={styles.stageIcon} src={stageIcon} />}
          required={required}
        />
      </FormItem>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  funnels: getFunnels(state),
  funnelStages: getFunnelStages(state),
});

export default connect(mapStateToProps)(FunnelStageField);
